/**
 * InvoiceDropdownActions - Acciones disponibles para cada factura
 */

import { faArrowDownToLine, faArrowUpRightFromSquare, faEye, faFileInvoice } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { usePermissions } from '@shared/hooks/usePermissions';
import { Invoice } from '../../models/Invoices';
import { isPendingContalinkInvoice } from '../../utils/invoiceAccounting';
import React from 'react';

interface InvoiceDropdownActionsProps {
  invoice: Invoice;
  onAction: (action: 'view' | 'download' | 'contalink', invoice: Invoice) => void;
}

export const InvoiceDropdownActions = ({ invoice, onAction }: InvoiceDropdownActionsProps) => {
  const { t } = useTranslation('invoicesModule');
  const { getTeamType, isHoldedAccounting, isContalinkAccounting } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  const viewHref = `/invoices/view/${invoice.id}`;
  const holdedUrl = invoice.erp_id ? `https://app.holded.com/sales/revenue#open:invoice-${invoice.erp_id}` : null;
  const canRegisterContalink = isTenant3pl && isContalinkAccounting() && isPendingContalinkInvoice(invoice);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`invoice-actions-${invoice.id}`}>
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem asChild>
          <a
            href={viewHref}
            data-testid="view-invoice-action"
            onClick={(event) => {
              if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
                return;
              }

              event.preventDefault();
              onAction('view', invoice);
            }}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                window.open(viewHref, '_blank', 'noopener');
              } else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onAction('view', invoice);
              }
            }}
          >
            <FontAwesomeIcon icon={faEye} className="mr-2 h-3.5 w-3.5 text-icons" />
            <span>{t('actions.view', 'Ver')}</span>
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onAction('download', invoice)} data-testid="download-invoice-action">
          <FontAwesomeIcon icon={faArrowDownToLine} className="mr-2 h-3.5 w-3.5 text-icons" />
          <span>{t('actions.download', 'Descargar')}</span>
        </DropdownMenuItem>

        {canRegisterContalink && (
          <DropdownMenuItem onClick={() => onAction('contalink', invoice)} data-testid="register-contalink-action">
            <FontAwesomeIcon icon={faFileInvoice} className="mr-2 h-3.5 w-3.5 text-icons" />
            <span>{t('actions.registerContalink', 'Registrar en Contalink')}</span>
          </DropdownMenuItem>
        )}

        {isTenant3pl && isHoldedAccounting() && holdedUrl && (
          <DropdownMenuItem asChild>
            <a
              href={holdedUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-holded-action"
              onClick={(event) => {
                if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
                  return;
                }
                event.preventDefault();
                window.open(holdedUrl, '_blank', 'noopener noreferrer');
              }}
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mr-2 h-3.5 w-3.5 text-icons" />
              <span>{t('actions.viewHolded', 'Ver en Holded')}</span>
            </a>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
